import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { Link } from '../../lib/router';
import { Loading } from '../../ui/components';

interface Plan {
  key: string; name: string; priceMonthCents: number | null; priceYearCents: number | null;
  maxLocations: number | null; maxMembers: number | null; maxStaff: number | null; features: string[]; highlight: boolean;
}
interface Offer { platformName: string; trialDays: number; signupOpen: boolean; plans: Plan[] }

const useOffer = (): Offer | null => {
  const [offer, setOffer] = useState<Offer | null>(null);
  useEffect(() => { void api<Offer>('/v1/public/plans').then(setOffer).catch(() => setOffer(null)); }, []);
  return offer;
};

/** The week the hero shows: a gym's own timetable, filling up. */
const WEEK: { day: number; start: number; length: number; key: string; booked: number; capacity: number }[] = [
  { day: 0, start: 0, length: 2, key: 'spinning', booked: 12, capacity: 16 },
  { day: 0, start: 5, length: 2, key: 'strength', booked: 9, capacity: 12 },
  { day: 1, start: 1, length: 2, key: 'yoga', booked: 14, capacity: 18 },
  { day: 1, start: 6, length: 2, key: 'bootcamp', booked: 20, capacity: 20 },
  { day: 2, start: 0, length: 2, key: 'spinning', booked: 16, capacity: 16 },
  { day: 2, start: 4, length: 3, key: 'pt', booked: 1, capacity: 1 },
  { day: 3, start: 2, length: 2, key: 'pilates', booked: 7, capacity: 12 },
  { day: 3, start: 6, length: 2, key: 'strength', booked: 11, capacity: 12 },
  { day: 4, start: 0, length: 2, key: 'bootcamp', booked: 18, capacity: 20 },
  { day: 4, start: 5, length: 2, key: 'yoga', booked: 10, capacity: 18 },
];

function Timetable() {
  const i18n = useI18n();
  const days = [0, 1, 2, 3, 4].map((d) => i18n.t(`sales.day_${d}`));
  return (
    <figure className="sales-week" aria-label={i18n.t('sales.week_label')}>
      <div className="sales-week-head">
        {days.map((d, i) => <span key={d} data-day={i}>{d}</span>)}
      </div>
      <div className="sales-week-grid">
        {WEEK.map((c, i) => {
          const full = c.booked >= c.capacity;
          return (
            <div key={i} data-day={c.day} className={`sales-class${full ? ' is-full' : ''}`}
              style={{ gridColumn: c.day + 1, gridRow: `${c.start + 1} / span ${c.length}` }}>
              <strong>{i18n.t(`sales.class_${c.key}`)}</strong>
              <span>{full ? (c.key === 'pt' ? i18n.t('sales.booked') : i18n.t('sales.full_waitlist', { count: 3 })) : `${c.booked}/${c.capacity}`}</span>
            </div>
          );
        })}
      </div>
    </figure>
  );
}

const money = (cents: number | null, locale: string): string =>
  cents === null ? '—' : new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(cents / 100);

/** The page a gym owner sees on the platform's own address. */
export function SalesPage() {
  const i18n = useI18n();
  const offer = useOffer();
  const [yearly, setYearly] = useState(false);
  const name = offer?.platformName ?? 'GymSuite';
  const locale = i18n.locale.startsWith('nl') ? 'nl-NL' : 'en-GB';
  const allFeatures = ['planning', 'memberships', 'payments', 'app', 'messages', 'website', 'crm', 'retention', 'training', 'coaching', 'inbody', 'locations', 'domain', 'support'];

  return (
    <div className="sales">
      <header className="sales-top">
        <span className="sales-mark">{name}</span>
        <nav>
          <a href="#prices">{i18n.t('sales.nav_prices')}</a>
          <Link to="/login">{i18n.t('sales.nav_login')}</Link>
          {offer?.signupOpen !== false && <Link className="sales-cta" to="/start">{i18n.t('sales.cta')}</Link>}
        </nav>
      </header>

      <main id="main">
        <section className="sales-hero">
          <div>
            <h1>{i18n.t('sales.title')}</h1>
            <p className="sales-lead">{i18n.t('sales.lead')}</p>
            <div className="sales-actions">
              {offer?.signupOpen !== false && <Link className="sales-cta sales-cta-lg" to="/start">{i18n.t('sales.cta_trial', { days: offer?.trialDays ?? 14 })}</Link>}
              <a className="sales-quiet" href="#prices">{i18n.t('sales.see_prices')}</a>
            </div>
            <p className="sales-note">{i18n.t('sales.no_card')}</p>
          </div>
          <Timetable />
        </section>

        <section className="sales-day">
          <h2>{i18n.t('sales.day_title')}</h2>
          <div className="sales-columns">
            {(['before', 'desk', 'month'] as const).map((k) => (
              <div key={k}>
                <h3>{i18n.t(`sales.${k}_title`)}</h3>
                <p>{i18n.t(`sales.${k}_text`)}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="sales-switch">
          <h2>{i18n.t('sales.switch_title')}</h2>
          <p>{i18n.t('sales.switch_text')}</p>
        </section>

        <section className="sales-prices" id="prices">
          <div className="sales-prices-head">
            <h2>{i18n.t('sales.prices_title')}</h2>
            <div className="sales-toggle" role="group" aria-label={i18n.t('sales.billing')}>
              <button type="button" aria-pressed={!yearly} onClick={() => setYearly(false)}>{i18n.t('sales.monthly')}</button>
              <button type="button" aria-pressed={yearly} onClick={() => setYearly(true)}>{i18n.t('sales.yearly')}</button>
            </div>
          </div>
          {!offer ? <Loading /> : (
            <div className="sales-table" role="table">
              {offer.plans.map((p, index) => (
                <div key={p.key} className={`sales-plan${p.highlight ? ' is-suggested' : ''}`} role="rowgroup">
                  <h3>{p.name}</h3>
                  {p.highlight && <p className="sales-suggested">{i18n.t('sales.suggested')}</p>}
                  <p className="sales-price">
                    {money(yearly ? (p.priceYearCents ?? 0) / 12 : p.priceMonthCents, locale)}
                    <small>{i18n.t('sales.per_month')}</small>
                  </p>
                  <p className="sales-limits">
                    {p.maxLocations === null ? i18n.t('sales.unlimited_locations') : i18n.t('sales.locations', { count: p.maxLocations })}
                    <br />
                    {p.maxMembers === null ? i18n.t('sales.unlimited_members') : i18n.t('sales.members', { count: p.maxMembers })}
                  </p>
                  {index > 0 && <p className="sales-plus">{i18n.t('sales.everything_in', { plan: offer.plans[index - 1].name })}</p>}
                  <ul>
                    {allFeatures.filter((f) => p.features.includes(f) && !(index > 0 && offer.plans[index - 1].features.includes(f)))
                      .map((f) => <li key={f}>{i18n.t(`sales.feature_${f}`)}</li>)}
                  </ul>
                  {offer.signupOpen && <Link className={p.highlight ? 'sales-cta' : 'sales-quiet'} to={`/start?plan=${p.key}`}>{i18n.t('sales.choose', { plan: p.name })}</Link>}
                </div>
              ))}
            </div>
          )}
          <p className="sales-note">{i18n.t('sales.vat_note')}</p>
        </section>

        <section className="sales-faq">
          <h2>{i18n.t('sales.faq_title')}</h2>
          {(['data', 'contract', 'payments', 'switch', 'members', 'privacy'] as const).map((k) => (
            <details key={k}>
              <summary>{i18n.t(`sales.q_${k}`)}</summary>
              <p>{i18n.t(`sales.a_${k}`)}</p>
            </details>
          ))}
        </section>
      </main>

      <footer className="sales-foot">
        <span>{name}</span>
        <nav aria-label={i18n.t('sales.legal')}>
          <Link to="/legal/terms">{i18n.t('sales.terms')}</Link>
          <Link to="/legal/dpa">{i18n.t('sales.dpa')}</Link>
          <Link to="/legal/privacy">{i18n.t('sales.privacy')}</Link>
          <Link to="/legal/subprocessors">{i18n.t('sales.subprocessors')}</Link>
          <Link to="/status">{i18n.t('sales.status')}</Link>
        </nav>
      </footer>
    </div>
  );
}

/** Just enough markdown for the legal documents: headings, paragraphs, lists, tables, quotes, bold, links. */
export function renderMarkdown(md: string): string {
  const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s: string): string => esc(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g, '<a href="$2">$1</a>');
  const lines = md.replace(/\r/g, '').split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) { out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`); i++; continue; }
    if (line.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        const cells = lines[i].split('|').slice(1, -1).map((c) => c.trim());
        if (!cells.every((c) => /^:?-+:?$/.test(c))) rows.push(cells);
        i++;
      }
      const [head, ...body] = rows;
      out.push(`<div class="legal-table"><table><thead><tr>${head.map((c) => `<th>${inline(c)}</th>`).join('')}</tr></thead><tbody>${body.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`);
      continue;
    }
    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { quote.push(lines[i].replace(/^>\s?/, '')); i++; }
      out.push(`<blockquote>${quote.filter(Boolean).map((q) => `<p>${inline(q)}</p>`).join('')}</blockquote>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && (/^[-*]\s+/.test(lines[i]) || /^\s{2,}\S/.test(lines[i]))) {
        if (/^[-*]\s+/.test(lines[i])) items.push(lines[i].replace(/^[-*]\s+/, ''));
        else items[items.length - 1] += ` ${lines[i].trim()}`;
        i++;
      }
      out.push(`<ul>${items.map((it) => `<li>${inline(it)}</li>`).join('')}</ul>`);
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && (/^\d+\.\s+/.test(lines[i]) || /^\s{2,}\S/.test(lines[i]))) {
        if (/^\d+\.\s+/.test(lines[i])) items.push(lines[i].replace(/^\d+\.\s+/, ''));
        else items[items.length - 1] += ` ${lines[i].trim()}`;
        i++;
      }
      out.push(`<ol>${items.map((it) => `<li>${inline(it)}</li>`).join('')}</ol>`);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\||>|[-*]\s|\d+\.\s)/.test(lines[i])) { para.push(lines[i].trim()); i++; }
    out.push(`<p>${inline(para.join(' '))}</p>`);
  }
  return out.join('\n');
}

/** A legal document on the website, in the visitor's language. */
export function LegalPage({ doc }: { doc: string }) {
  const i18n = useI18n();
  const [data, setData] = useState<{ markdown: string; version: string } | null | 'missing'>(null);
  useEffect(() => {
    setData(null);
    void api<{ markdown: string; version: string }>(`/v1/public/legal/${encodeURIComponent(doc)}`, { query: { lang: i18n.locale } })
      .then(setData).catch(() => setData('missing'));
  }, [doc, i18n.locale]);
  return (
    <div className="sales">
      <header className="sales-top">
        <Link className="sales-mark" to="/">{i18n.t('sales.back_home')}</Link>
        <nav aria-label={i18n.t('sales.legal')}>
          <Link to="/legal/terms">{i18n.t('sales.terms')}</Link>
          <Link to="/legal/dpa">{i18n.t('sales.dpa')}</Link>
          <Link to="/legal/privacy">{i18n.t('sales.privacy')}</Link>
        </nav>
      </header>
      <main id="main" className="legal">
        {data === null ? <Loading /> : data === 'missing'
          ? <><h1>{i18n.t('notfound.title')}</h1><p><Link to="/">{i18n.t('sales.back_home')}</Link></p></>
          : <article dangerouslySetInnerHTML={{ __html: renderMarkdown(data.markdown) }} />}
      </main>
    </div>
  );
}
